import {
  getFrameShadow,
  getInverseFrameHighlight,
} from 'components/Tile/shadows'
import {
  ASSET_RELATIVE_BORDER,
  BOTTOM_ROTATION_DEGREES,
  RIGHT_ROTATION_DEGREES,
} from 'components/Tile/CanvasTile/CanvasTile.consts'
import { getCanvas3DAssetUrl, getCanvasTextureAssetUrl } from 'assets/tiles'
import { sin } from 'utils/utils'
import framelessDepthUrl from '@images/frame-assets/frameless.png'
import { styled, css } from 'styled-components'

// getFrameShadow() is CSS-ready with a trailing semicolon; strip it so the
// value can also be used in inline styles.
export const FRAME_BOX_SHADOW = getFrameShadow().replace(/;\s*$/, '')

// White frames need shadow definition on their light-facing (top-left) side too,
// so a white tile stays separated against a light background.
export const WHITE_FRAME_BOX_SHADOW = `${FRAME_BOX_SHADOW}, ${getInverseFrameHighlight()}`

// Fractions of the tile's longer edge, tuned on the 80px cart thumbnail
// (5px depth, 3.5px offset; 2px / 1px shadow inset).
const FRAMELESS_DEPTH_WIDTH_RATIO = 5 / 80
const FRAMELESS_DEPTH_DISTANCE_RATIO = 3.5 / 80
const CANVAS_SHADOW_RIGHT_RATIO = 2 / 80
const CANVAS_SHADOW_BOTTOM_RATIO = 1 / 80

const frameShadow = ($includeShadow: boolean) => css`
  box-shadow: ${$includeShadow ? FRAME_BOX_SHADOW : 'none'};
`

// --tile-longer-edge stays unresolved until a descendant reads it, so its cqw
// measures the AspectBox. Rounded because a portrait box's width carries
// sub-pixel layout error, which would move the frameless depth's .5px edges.
const tileLongerEdge = (aspectRatio: number) => {
  const longerEdge = `100cqw / ${Math.min(aspectRatio, 1)}`
  return css`
    --tile-longer-edge: calc(${longerEdge});
    @supports (width: round(1px, 1px)) {
      --tile-longer-edge: round(${longerEdge}, 1px);
    }
  `
}

// Shared tile box: sized to its aspect ratio while fitting inside its
// container, and the size container its layers measure with cqw.
export const AspectBox = styled.div<{ $aspectRatio: number }>`
  position: relative;
  box-sizing: border-box;
  container-type: inline-size;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  ${({ $aspectRatio }) => tileLongerEdge($aspectRatio)}
  ${({ $aspectRatio }) =>
    $aspectRatio >= 1
      ? css`
          width: 100%;
          max-height: 100%;
        `
      : css`
          height: 100%;
          max-width: 100%;
        `}
`

// --tile-longer-edge's cqw resolves against an ancestor container, never the
// element itself, so the frame is a layer inside AspectBox. Browsers floor a
// fractional border width to whole device pixels; round() keeps the nearest
// whole px instead.
export const FrameLayer = styled.div`
  position: absolute;
  inset: 0;
  border-style: solid;
  border-color: transparent;
  border-width: max(
    var(--frame-min-width),
    calc(var(--frame-width-ratio) * var(--tile-longer-edge))
  );
  @supports (width: round(1px, 1px)) {
    border-width: max(
      var(--frame-min-width),
      round(var(--frame-width-ratio) * var(--tile-longer-edge), 1px)
    );
  }
  border-image-width: 1;
`

// Using absolute positioning to fill the container so Safari respects the
// tile's aspect ratio (otherwise it would stretch to fill the thumbnail box).
export const Matting = styled.div`
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`

// Clips the photo to its container so the crop overflow stays hidden. The
// frameless/canvas variants extend this with their own positioning.
export const PhotoClip = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`

export const CroppedPhoto = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
  display: block;
`

export const PhotoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.grayBright};
`

// Frameless tiles have no frame or matting: the photo sticks to the top-left
// corner and the tile's depth (thickness) only shows on the right and bottom,
// mirroring the real frameless tile (see TileFlatFramelessDepth). The depth box
// sits behind the photo, offset past the bottom-right edge.
export const FramelessDepth = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: calc(-${FRAMELESS_DEPTH_DISTANCE_RATIO} * var(--tile-longer-edge));
  bottom: calc(-${FRAMELESS_DEPTH_DISTANCE_RATIO} * var(--tile-longer-edge));
  border-style: solid;
  border-width: calc(${FRAMELESS_DEPTH_WIDTH_RATIO} * var(--tile-longer-edge));
  border-image: url(${framelessDepthUrl}) 24 stretch;
`

// Fills the AspectBox via absolute positioning (not the inherited width/height:
// 100%) for the same Safari aspect-ratio reason as Matting above.
export const FramelessPhoto = styled(PhotoClip)<{ $includeShadow: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  ${({ $includeShadow }) => frameShadow($includeShadow)}
`

// Canvas tiles have no frame or matting. The 3D look (angled right/bottom sides
// + canvas texture) reproduces the real canvas tile's Canvas3D / CanvasTexture
// overlays multiplied under the cropped photo (see CanvasTile).
const fillOverlay = css`
  position: absolute;
  inset: 0;
  overflow: hidden;
`

export const CanvasMask = styled.div`
  ${fillOverlay}
`

export const CanvasPhotoClip = styled.div`
  ${fillOverlay}
  mix-blend-mode: multiply;
`

function calculateTextureSidesSize({
  $relativeRightSideSize,
  $relativeBottomSideSize,
}: {
  $relativeRightSideSize: number
  $relativeBottomSideSize: number
}) {
  const rightSideSize = sin(RIGHT_ROTATION_DEGREES) * $relativeRightSideSize
  const bottomSideSize = sin(BOTTOM_ROTATION_DEGREES) * $relativeBottomSideSize
  return `0 ${100 * rightSideSize}% ${100 * bottomSideSize}% 0;`
}

export const Canvas3D = styled.div<{
  $relativeRightSideSize: number
  $relativeBottomSideSize: number
}>`
  position: absolute;
  width: 100%;
  height: 100%;
  border-image-source: url('${getCanvas3DAssetUrl()}');
  border-image-slice: 0 ${100 * ASSET_RELATIVE_BORDER.right}%
    ${100 * ASSET_RELATIVE_BORDER.bottom}% ${100 * ASSET_RELATIVE_BORDER.left}%
    fill;
  border-image-width: ${calculateTextureSidesSize};
`

export const CanvasTexture = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-image-source: url('${getCanvasTextureAssetUrl()}');
  border-image-slice: 0 fill;
  mix-blend-mode: multiply;
  transform: scale(1.5);
`

export const CanvasShadow = styled.div`
  position: absolute;
  right: calc(${CANVAS_SHADOW_RIGHT_RATIO} * var(--tile-longer-edge));
  bottom: calc(${CANVAS_SHADOW_BOTTOM_RATIO} * var(--tile-longer-edge));
  box-shadow: ${FRAME_BOX_SHADOW};
`
